import { useState, useEffect } from "react";
import { MOODS, MOOD_BY_ID } from "@/data/checkInData";
import { api } from "@/lib/api";
import BookingButton from "./BookingButton";
import { RotateCw } from "lucide-react";

const HISTORY_KEY = "sano_checkin_history";

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); }
  catch { return []; }
}
function saveHistory(entries) {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(-30)));
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export default function BodyCheckIn() {
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState(loadHistory());

  useEffect(() => {
    // If they already checked in today, pre-select
    const today = history.find((h) => h.day === todayKey());
    if (today) setSelected(today.mood);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (id) => {
    setSelected(id);
    const entries = [...history.filter((h) => h.day !== todayKey()), { day: todayKey(), mood: id, ts: Date.now() }];
    saveHistory(entries);
    setHistory(entries);
    try { await api.post("/checkin", { mood: id }); } catch (err) { console.error("Checkin save failed:", err); /* non-blocking */ }
  };

  const reset = () => setSelected(null);
  const mood = selected ? MOOD_BY_ID[selected] : null;

  return (
    <section
      id="checkin"
      className="py-28 lg:py-36 border-y"
      style={{ background: "var(--sano-bg)", borderColor: "var(--sano-border)" }}
      data-testid="checkin-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">08 · Body Check-In</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight">
              How is your body <em className="italic" style={{ color: "var(--sano-olive)" }}>today</em>?
            </h2>
            <p className="mt-4 text-base leading-relaxed max-w-xl" style={{ color: "var(--sano-ink-2)" }}>
              30 seconds. Pick the closest word. We&apos;ll give you one ritual, one
              point, and one mantra you can do right now.
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end items-end">
            <MoodWeather history={history} />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="checkin-moods">
          {MOODS.map((m) => (
            <button
              key={m.id}
              onClick={() => pick(m.id)}
              className="text-left border p-5 transition-all hover:bg-[#F0EBE1]"
              style={{
                borderColor: selected === m.id ? m.color : "var(--sano-border)",
                background: selected === m.id ? "var(--sano-bg-2)" : "var(--sano-bg)",
                outline: selected === m.id ? `1px solid ${m.color}` : "none",
              }}
              data-testid={`checkin-mood-${m.id}`}
            >
              <span
                className="inline-block h-2.5 w-2.5 rounded-full mb-3"
                style={{ background: m.color }}
              />
              <div className="font-serif-display text-2xl leading-tight">{m.label}</div>
              <div className="text-xs mt-1" style={{ color: "var(--sano-ink-2)" }}>{m.hint}</div>
            </button>
          ))}
        </div>

        {mood && (
          <div
            className="mt-10 p-10 lg:p-12 border grid lg:grid-cols-12 gap-10"
            style={{ background: "var(--sano-bg-2)", borderColor: "var(--sano-border)" }}
            data-testid="checkin-result"
          >
            <div className="lg:col-span-5">
              <div className="eyebrow mb-3" style={{ color: mood.color }}>
                {mood.label} · {mood.element}
              </div>
              <h3 className="font-serif-display text-3xl mb-4">Your 2-minute ritual</h3>
              <p className="text-base leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>
                {mood.ritual}
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="eyebrow mb-3">Press this point</div>
              <div className="font-serif-display text-2xl mb-2">{mood.point.n}</div>
              <p className="text-sm leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>{mood.point.how}</p>
            </div>
            <div className="lg:col-span-3">
              <div className="eyebrow mb-3">Today&apos;s mantra</div>
              <div className="font-serif-display text-xl italic leading-snug mb-6">“{mood.mantra}”</div>
              <BookingButton testid="checkin-book" size="sm" label="Go deeper — book a session" />
              <button
                onClick={reset}
                className="mt-3 inline-flex items-center gap-1.5 text-xs hover:underline"
                style={{ color: "var(--sano-ink-2)" }}
                data-testid="checkin-reset"
              >
                <RotateCw size={12} strokeWidth={1.5} /> Choose a different word
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function MoodWeather({ history }) {
  if (history.length === 0) return null;
  const last14 = history.slice(-14);
  return (
    <div data-testid="checkin-weather">
      <div className="eyebrow mb-3 text-right">Your last {last14.length} days</div>
      <div className="flex gap-1.5 items-end">
        {last14.map((h, i) => {
          const m = MOOD_BY_ID[h.mood];
          return (
            <div
              key={i}
              title={`${m?.label || h.mood} · ${new Date(h.ts).toLocaleDateString()}`}
              className="h-8 w-3 rounded-sm"
              style={{ background: m?.color || "var(--sano-border)" }}
              data-testid={`checkin-weather-${i}`}
            />
          );
        })}
      </div>
      <div className="mt-2 text-xs text-right" style={{ color: "var(--sano-ink-2)" }}>
        Stored privately on this device.
      </div>
    </div>
  );
}
